import React, {useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import Grid from "@mui/material/Grid2";
import moment from "moment-timezone";
import {PanierRepasIcon} from "../../../../utils/ui/Icons/PanierRepasIcon.js";
import Constants from "../../../../utils/constants.js";
import {ALERTE_LABELS, formatMinutes, getCellColors, hasHS, JOURS_LABELS} from "./validationHelpers.js";

// Ligne récapitulative des totaux de la semaine
function StatsRow({totaux}) {
    if (!totaux) return null;
    const cells = [
        {label: "Normal", tip: "Heures normales retenues (base 35h/semaine)", value: formatMinutes((totaux.hn || 0) * 60), colorProp: "text.primary"},
        {label: "Travaillé", tip: "Heures de jour réellement travaillées", value: formatMinutes((totaux.totalJour || 0) * 60), colorProp: "text.primary"},
        ...(totaux.totalNuit > 0 ? [{label: "Nuit", tip: "Heures de nuit (21h-6h), majorées à 100%", value: formatMinutes((totaux.totalNuit || 0) * 60), colorProp: "info.main"}] : []),
        {label: "Total", tip: "Heures jour + nuit de la semaine", value: formatMinutes((totaux.total || 0) * 60), colorProp: "success.main"},
        {label: "Sup.", tip: "Heures au-delà de 35h (majorées 25% puis 50%)", value: totaux.hsMinutes > 0 ? formatMinutes(totaux.hsMinutes) : "—", colorProp: totaux.hsMinutes > 0 ? "warning.dark" : "text.disabled"},
    ];
    return (
        <Box display="flex" sx={{border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", mb: 1}}>
            {cells.map((c, i) => (
                <Box key={c.label} sx={{
                    flex: 1, textAlign: "center", px: 1, py: 1,
                    borderLeft: i > 0 ? "1px solid" : "none", borderColor: "divider",
                }}>
                    <Tooltip title={c.tip} arrow>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{cursor: "help", textDecoration: "underline dotted", textDecorationColor: "rgba(0,0,0,0.2)"}}>
                            {c.label}
                        </Typography>
                    </Tooltip>
                    <Typography variant="body2" fontWeight={700} color={c.colorProp}>{c.value}</Typography>
                </Box>
            ))}
        </Box>
    );
}

// Bloc accordéon d'une semaine : en-tête, grille des jours, totaux et actions
export default function SemaineBlock({semaine, onDayClick, onToggleDeplacement, onValidate, onDevalidate, isAdmin, validating, togglingDeplacementKey}) {
    const isValidated = semaine.statutValidation === "valide";
    const isToggling = togglingDeplacementKey === semaine.debut;
    const totaux = semaine.totaux;
    const totalMin = (totaux?.total || 0) * 60;
    const hsMin = totaux?.hsMinutes || 0;
    const isHighVolume = totalMin > 45 * 60;
    // Les semaines déjà validées arrivent repliées : l'attention va au travail restant
    const [expanded, setExpanded] = useState(semaine.statutValidation !== "valide");

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{
                mb: 2,
                border: isValidated ? "2px solid" : "1px solid",
                borderColor: isValidated ? "success.main" : "divider",
                backgroundColor: isValidated ? "#f0faf4" : "#fff",
                "&:before": {display: "none"},
                borderRadius: "4px !important",
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon/>}
                sx={{backgroundColor: isValidated ? "#e6f7ee" : "#f5f5f5"}}
            >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{width: "100%", pr: 1}}>
                    <Typography variant="h6">
                        Semaine {semaine.numero} — {moment(semaine.debut).format("DD/MM")} au {moment(semaine.fin).format("DD/MM")}
                    </Typography>
                    {semaine.clients?.map(c => (
                        <Chip key={c} label={c} size="small"/>
                    ))}
                    {semaine.alertes?.map(a => (
                        <Chip key={a} label={ALERTE_LABELS[a] || a} color="warning" size="small" variant="outlined"/>
                    ))}
                    {isHighVolume && (
                        <Chip icon={<WarningAmberIcon/>} label="Volume élevé" color="error" size="small" variant="outlined"/>
                    )}
                    {hsMin > 0 && (
                        <Chip label={`+${formatMinutes(hsMin)} HS`} color={isValidated ? "success" : "warning"} size="small"/>
                    )}
                    {isValidated && (
                        <Chip icon={<CheckIcon/>} label="Validée" color="success" size="small" sx={{ml: "auto"}}/>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{p: 2}}>
                {/* Grille des jours */}
                <Grid container spacing={1} mb={1}>
                    {JOURS_LABELS.map((label, idx) => {
                        const jourDate = moment(semaine.debut).add(idx, "days").format("YYYY-MM-DD");
                        const jour = semaine.jours?.find(j => j.date === jourDate) || null;
                        const {bg, fg} = getCellColors(jour);
                        const hasOvertime = hasHS(jour);
                        const isClickable = !!jour?.posteOperateurId;
                        // Libellé humain du statut pour le tooltip (et non le code brut de l'enum)
                        const statutLabel = jour?.statutJournee
                            ? (Constants.RH_STATUT_JOURNEE_DATA?.find(o => o.id === (jour.statutJournee === "RTT" ? "Repos" : jour.statutJournee))?.label || jour.statutJournee)
                            : "";

                        return (
                            <Grid size={12 / 7} key={label}>
                                <Box sx={{textAlign: "center"}}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        {label} {moment(semaine.debut).add(idx, "days").format("DD/MM")}
                                    </Typography>
                                    <Tooltip title={statutLabel} disableHoverListener={!jour?.exists}>
                                        <Paper
                                            variant="outlined"
                                            onClick={() => isClickable && onDayClick(jour)}
                                            role={isClickable ? "button" : undefined}
                                            tabIndex={isClickable ? 0 : -1}
                                            onKeyDown={(e) => {
                                                if (isClickable && (e.key === "Enter" || e.key === " ")) {
                                                    e.preventDefault();
                                                    onDayClick(jour);
                                                }
                                            }}
                                            sx={{
                                                p: 1.5,
                                                backgroundColor: bg,
                                                color: fg,
                                                cursor: isClickable ? "pointer" : "default",
                                                outline: hasOvertime ? "2px solid" : "none",
                                                outlineColor: hasOvertime ? "warning.main" : "transparent",
                                                position: "relative",
                                                textAlign: "center",
                                            }}
                                        >
                                            {jour?.validatedAt && (
                                                <CheckIcon sx={{position: "absolute", top: 4, left: 4, fontSize: 12, color: "info.main"}}/>
                                            )}
                                            {jour?.panierRepas && (
                                                <PanierRepasIcon style={{position: "absolute", top: 2, right: 2, fontSize: 14, opacity: 0.9}}/>
                                            )}
                                            {jour?.alerte && (jour.alerte.types || []).some(t => t !== "idle_prolonge") && (
                                                <Box
                                                    sx={{
                                                        position: "absolute", bottom: 4, right: 4,
                                                        width: 10, height: 10, borderRadius: "50%",
                                                        backgroundColor: jour.alerte.maxSeverity === "critical" ? "#d32f2f" : "#ef6c00",
                                                    }}
                                                    title={`${jour.alerte.count} alerte(s) RH : ${(jour.alerte.types || []).filter(t => t !== "idle_prolonge").map(t => ALERTE_LABELS[t] || t).join(", ")}`}
                                                />
                                            )}
                                            {jour?.alerte && (jour.alerte.types || []).includes("idle_prolonge") && (
                                                <Tooltip title="Arrêt moteur prolongé pendant le poste" arrow>
                                                    <LocalShippingIcon sx={{position: "absolute", bottom: 2, left: 2, fontSize: 15, color: "#d32f2f"}}/>
                                                </Tooltip>
                                            )}
                                            {jour?.exists ? (
                                                <Typography variant="body1" fontWeight={500}>
                                                    {jour.statutJournee === "JFT"
                                                        ? `JFT · ${formatMinutes(jour.jourMinutes)}`
                                                        : jour.statutJournee && jour.statutJournee !== "WD"
                                                            ? (Constants.RH_STATUT_JOURNEE_DATA?.find(o => o.id === (jour.statutJournee === "RTT" ? "Repos" : jour.statutJournee))?.label || jour.statutJournee)
                                                            : formatMinutes(jour.jourMinutes)
                                                    }
                                                    {jour?.nuitMinutes > 0 && (
                                                        <Chip sx={{ml: 1}} size="small" color="info" label={`${formatMinutes(jour.nuitMinutes)} nuit`}/>
                                                    )}
                                                </Typography>
                                            ) : (
                                                <Typography variant="body1" color="text.disabled">—</Typography>
                                            )}
                                        </Paper>
                                    </Tooltip>

                                    {jour?.nbBacs != null && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {jour.nbBacs} bacs
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Ligne stats */}
                <StatsRow totaux={totaux}/>

                {/* Footer */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                    {/* Toggle déplacement */}
                    <Box display="flex" alignItems="center" gap={1}>
                        <Switch
                            checked={!!semaine.deplacement}
                            onChange={() => onToggleDeplacement(semaine)}
                            disabled={isToggling}
                            color="success"
                        />
                        <Box>
                            <Typography variant="body2">Déplacement</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {semaine.deplacement ? "Forfait activé" : "Pas de forfait"}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Bouton validation */}
                    {isValidated ? (
                        /* L'état validé est déjà porté par le chip du header et le fond vert,
                           on ne garde ici que l'action d'annulation (admin) */
                        isAdmin ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onDevalidate(semaine)}
                                disabled={validating === semaine.debut}
                            >
                                {validating === semaine.debut ? <CircularProgress size={14}/> : "Annuler la validation"}
                            </Button>
                        ) : <Box/>
                    ) : (
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={validating === semaine.debut ? <CircularProgress size={16} sx={{color: "white"}}/> : <CheckIcon/>}
                            onClick={() => onValidate(semaine)}
                            disabled={!!validating}
                        >
                            Valider
                        </Button>
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}
